import React, { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { Prompt } from 'next/font/google';
import { GoogleAnalytics } from '@next/third-parties/google';
import ClientOnly from '../components/ClientOnly';
import ErrorBoundary from '../components/ErrorBoundary';
import Navbar from '../components/Navbar';
import NextNProgress from 'nextjs-progressbar';
import '../styles/globals.css';
import { onCookieConsentChange, readCookieConsent } from '../utils/cookieConsent';

const prompt = Prompt({
  subsets: ['thai', 'latin'],
  // Load only 2 critical weights to reduce font preload bandwidth (was 4 weights = 8 preloads)
  weight: ['400', '700'],
  display: 'optional',
  adjustFontFallback: true,
  fallback: ['system-ui', '-apple-system', 'BlinkMacSystemFont', 'Segoe UI', 'sans-serif'],
  preload: true,
  variable: '--font-prompt',
});

// Dynamic imports keep heavy UI out of the initial bundle and avoid hydration mismatches
const Footer = dynamic(() => import('../components/Footer'), {
  loading: () => (
    <footer className="min-h-[300px] bg-white border-t border-gray-200" aria-hidden="true" />
  ),
});
const CookieConsent = dynamic(() => import('../components/CookieConsent'), {
  ssr: false,
  loading: () => null,
});
const MobileBottomNav = dynamic(() => import('../components/MobileBottomNav'), {
  ssr: false,
  loading: () => null,
});

export default function MyApp({ Component, pageProps, router }) {
  // Use router from props instead of useRouter() hook to prevent NextRouter was not mounted during SSG
  const path = router?.asPath || router?.pathname || '';
  const isAdminRoute = path.startsWith('/admin');

  const [cookieConsent, setCookieConsent] = useState(null);

  // Defer Vercel analytics tooling so it doesn't compete with hydration/bootup.
  const [VercelTools, setVercelTools] = useState(null);

  // Defer Google Analytics so it doesn't block the main thread initially
  const [analyticsReady, setAnalyticsReady] = useState(false);

  useEffect(() => {
    // Delay heavy 3rd party scripts (GTM) until the user interacts or a max timeout.
    // This dramatically improves the INP score and frees up the Main Thread.
    if (typeof window === 'undefined') return;

    let timeoutId;
    const run = () => {
      setAnalyticsReady(true);
      // Clean up listeners once fired
      window.removeEventListener('scroll', run);
      window.removeEventListener('mousemove', run);
      window.removeEventListener('touchstart', run);
      window.removeEventListener('keydown', run);
      if (timeoutId) window.clearTimeout(timeoutId);
    };

    // Load on first interaction (passive to avoid scrolling jank)
    window.addEventListener('scroll', run, { passive: true, once: true });
    window.addEventListener('mousemove', run, { passive: true, once: true });
    window.addEventListener('touchstart', run, { passive: true, once: true });
    window.addEventListener('keydown', run, { passive: true, once: true });

    // Fallback: Load anyway after 8 seconds if no interaction occurs
    timeoutId = window.setTimeout(run, 8000);

    return () => {
      window.removeEventListener('scroll', run);
      window.removeEventListener('mousemove', run);
      window.removeEventListener('touchstart', run);
      window.removeEventListener('keydown', run);
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, []);

  // Initialize performance monitoring (Web Vitals + custom observers) without competing with hydration.
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const run = () => {
      import('../lib/performance')
        .then(mod => {
          if (typeof mod.initPerformanceObserver === 'function') {
            mod.initPerformanceObserver();
          }
        })
        .catch(() => {
          // silent fail - performance monitoring is non-critical
        });
    };

    let idleId;
    let timeoutId;
    if ('requestIdleCallback' in window) {
      idleId = window.requestIdleCallback(run, { timeout: 2500 });
    } else {
      timeoutId = window.setTimeout(run, 0);
    }

    return () => {
      if (idleId && window.cancelIdleCallback) window.cancelIdleCallback(idleId);
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, []);

  // Read stored cookie consent + react to changes from the banner.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    setCookieConsent(readCookieConsent());
    return onCookieConsentChange(next => setCookieConsent(next));
  }, []);

  // Defer Vercel Analytics + Speed Insights into an idle-loaded async chunk.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (process.env.NODE_ENV !== 'production') return;
    if (isAdminRoute) return;

    let cancelled = false;

    const run = () => {
      // Load Analytics only if consented; Load SpeedInsights unconditionally (no cookies used)
      const tasks = [
        cookieConsent?.analytics ? import('@vercel/analytics/react') : Promise.resolve(null),
        import('@vercel/speed-insights/next'),
      ];

      Promise.all(tasks)
        .then(([analyticsMod, speedMod]) => {
          if (cancelled) return;
          const Analytics = analyticsMod?.Analytics;
          const SpeedInsights = speedMod?.SpeedInsights;
          if (!Analytics && !SpeedInsights) return;

          setVercelTools(() => {
            return function VercelToolsLoaded() {
              return (
                <>
                  {Analytics ? <Analytics /> : null}
                  {SpeedInsights ? <SpeedInsights /> : null}
                </>
              );
            };
          });
        })
        .catch(() => {
          // silent fail
        });
    };

    let idleId;
    let timeoutId;
    if ('requestIdleCallback' in window) {
      idleId = window.requestIdleCallback(run, { timeout: 4000 });
    } else {
      timeoutId = window.setTimeout(run, 1500);
    }

    return () => {
      cancelled = true;
      if (idleId && window.cancelIdleCallback) window.cancelIdleCallback(idleId);
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [isAdminRoute, cookieConsent?.analytics]);

  // Defer non-critical client-only runtime guards to an async chunk
  useEffect(() => {
    if (typeof window === 'undefined') return;

    let cleanup;
    const run = () => {
      import('../lib/clientRuntime')
        .then(mod => {
          if (typeof mod.initClientRuntime === 'function') {
            cleanup = mod.initClientRuntime();
          }
        })
        .catch(() => {
          // silent fail - compatibility guards are non-critical
        });
    };

    let idleId;
    let timeoutId;
    if ('requestIdleCallback' in window) {
      idleId = window.requestIdleCallback(run, { timeout: 1500 });
    } else {
      timeoutId = window.setTimeout(run, 0);
    }

    return () => {
      if (cleanup) cleanup();
      if (idleId && window.cancelIdleCallback) window.cancelIdleCallback(idleId);
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, []);

  // Defer service worker registration so it doesn't compete with hydration
  useEffect(() => {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;

    const run = () => {
      import('../lib/clientRuntime')
        .then(mod => {
          if (typeof mod.registerServiceWorker === 'function') {
            mod.registerServiceWorker();
          }
        })
        .catch(() => {
          // silent fail
        });
    };

    let idleId;
    let timeoutId;
    if ('requestIdleCallback' in window) {
      idleId = window.requestIdleCallback(run, { timeout: 3000 });
    } else {
      timeoutId = window.setTimeout(run, 1000);
    }

    return () => {
      if (idleId && window.cancelIdleCallback) window.cancelIdleCallback(idleId);
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, []);

  // Check if page has a custom layout
  const getLayout =
    Component.getLayout ||
    (page => {
      // Default layout for public pages
      return (
        <div className={`${prompt.variable} font-prompt flex flex-col min-h-screen`}>
          <NextNProgress
            color="#1a237e"
            startPosition={0.3}
            stopDelayMs={200}
            height={4}
            showOnShallow={true}
            options={{ showSpinner: false }}
          />
          <Navbar router={router} />
          <main id="main" role="main" className="flex-1 w-full relative flex flex-col">
            {page}
          </main>
          <Footer />
          {!isAdminRoute && (
            <>
              <MobileBottomNav />
              <ClientOnly>
                <CookieConsent />
              </ClientOnly>
            </>
          )}
        </div>
      );
    });

  return (
    <>
      <Head>
        <meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover" />
      </Head>
      <ErrorBoundary>{getLayout(<Component {...pageProps} router={router} />)}</ErrorBoundary>
      {VercelTools ? <VercelTools /> : null}
      {!isAdminRoute &&
        process.env.NODE_ENV === 'production' &&
        cookieConsent?.analytics &&
        analyticsReady && <GoogleAnalytics gaId="G-6X6MT38ML7" strategy="lazyOnload" />}
    </>
  );
}

// Report web vitals for better SEO performance monitoring
export function reportWebVitals(metric) {
  if (typeof window === 'undefined') return;

  // Optional dev logging
  if (process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.log('Web Vitals:', metric);
  }

  // Delegate to performance helper (dynamic import keeps it out of main bundle)
  import('../lib/performance')
    .then(({ checkWebVitals }) => {
      if (typeof checkWebVitals === 'function') {
        checkWebVitals(metric);
      }
    })
    .catch(() => {
      // Fallback to GA if available
      if (window.gtag) {
        window.gtag('event', metric.name, {
          value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
          event_category: 'Web Vitals',
          event_label: metric.id,
          non_interaction: true,
        });
      }
    });
}
